// The panel's "N Filters Active" count (monday 13121483272). Pure so it can be tested.

// Mock location data for display labels (should match CriteriaDrawer)
const mockLocationData = {
  city: [
    { label: 'New York', value: 'new_york' },
    { label: 'Los Angeles', value: 'los_angeles' },
    { label: 'Chicago', value: 'chicago' },
    { label: 'Houston', value: 'houston' },
  ],
  county: [
    { label: 'Los Angeles County', value: 'los_angeles_county' },
    { label: 'Cook County', value: 'cook_county' },
    { label: 'Harris County', value: 'harris_county' },
  ],
  state: [
    { label: 'California', value: 'california' },
    { label: 'New York', value: 'new_york' },
    { label: 'Texas', value: 'texas' },
    { label: 'Florida', value: 'florida' },
  ],
  zip_code: [
    { label: '90210', value: '90210' },
    { label: '10001', value: '10001' },
    { label: '60601', value: '60601' },
  ],
  area: [
    { label: 'Downtown', value: 'downtown' },
    { label: 'Midtown', value: 'midtown' },
    { label: 'Uptown', value: 'uptown' },
  ],
  subarea: [
    { label: 'Financial District', value: 'financial_district' },
    { label: 'Arts District', value: 'arts_district' },
    { label: 'Theater District', value: 'theater_district' },
  ],
};

export interface CriteriaConfig {
  // Listing settings
  maxListings?: number;

  // Market criteria
  criteriaType?: 'map' | 'area';
  locationType?: 'city' | 'county' | 'state' | 'zip_code' | 'area' | 'subarea';
  city?: string;
  county?: string;
  state?: string;
  zip_code?: string;
  area?: string;
  subarea?: string;

  // Map specific
  address?: string;
  lat?: number;
  lng?: number;
  zoomLevel?: number;

  // Property selection
  propertyType?: 'residential' | 'multifamily' | 'commercial' | 'land';
  propertyStyles?: string[];

  // Range filters
  priceMin?: number;
  priceMax?: number;
  sqftMin?: number;
  sqftMax?: number;
  bedsMin?: number;
  bedsMax?: number;
  bathsMin?: number;
  bathsMax?: number;
  unitsMin?: number;
  unitsMax?: number;
  buildingsMin?: number;
  buildingsMax?: number;
  acreageMin?: number;
  acreageMax?: number;
  yearBuilt?: number; // Single year built field - means "built in year X or after"
  officeSqftMin?: number;
  officeSqftMax?: number;

  // Features/amenities
  golfCourse?: boolean;
  waterFront?: boolean;
  propertyView?: boolean;
}

const propertyTypeLabels: Record<string, string> = {
  residential: 'Residential',
  multifamily: 'Multifamily',
  commercial: 'Commercial',
  land: 'Land',
};

const locationTypeLabels: Record<string, string> = {
  city: 'City',
  county: 'County',
  state: 'State',
  zip_code: 'ZIP Code',
  area: 'Area',
  subarea: 'Sub-Area',
};

const toNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

/** One human-readable line per active filter; its length is the filter count. */
export function getCriteriaFilterSummary(criteriaConfig: CriteriaConfig & Record<string, any>): string[] {
  const filters: string[] = [];

  // Property type
  if (criteriaConfig.propertyType) {
    filters.push(
      `Type: ${
        propertyTypeLabels[criteriaConfig.propertyType] || criteriaConfig.propertyType
      }`,
    );
  }

  // Location criteria
  if (criteriaConfig.criteriaType === 'map' && criteriaConfig.address) {
    filters.push(`Location: ${criteriaConfig.address}`);
  } else if (criteriaConfig.criteriaType === 'map' && (criteriaConfig.lat || criteriaConfig.lng)) {
    filters.push(`Location: Map (${criteriaConfig.lat?.toFixed(4)}, ${criteriaConfig.lng?.toFixed(4)})`);
  } else if (
    criteriaConfig.criteriaType === 'area' &&
    criteriaConfig.locationType &&
    criteriaConfig[criteriaConfig.locationType]
  ) {
    const locationValue = criteriaConfig[criteriaConfig.locationType];
    const locationLabel =
      locationTypeLabels[criteriaConfig.locationType] || criteriaConfig.locationType;

    // Get display label from mockLocationData
    const locationOptions =
      mockLocationData[
        criteriaConfig.locationType as keyof typeof mockLocationData
      ] || [];
    const selectedOption = locationOptions.find(opt => opt.value === locationValue);
    const displayValue = selectedOption ? selectedOption.label : locationValue;

    filters.push(`${locationLabel}: ${displayValue}`);
  } else {
    // Individual location fields from the shared form - also list mode ('area')
    // with no locationType, which is every drawer save (monday 13121483272).
    const locationParts = [];
    if (criteriaConfig.city) locationParts.push(criteriaConfig.city);
    if (criteriaConfig.county) locationParts.push(criteriaConfig.county);
    if (criteriaConfig.state) locationParts.push(criteriaConfig.state);
    if (criteriaConfig.zip_code) locationParts.push(criteriaConfig.zip_code);
    if (criteriaConfig.area) locationParts.push(`Area: ${criteriaConfig.area}`);
    if (criteriaConfig.subarea) locationParts.push(`Sub-area: ${criteriaConfig.subarea}`);
    if (locationParts.length > 0) {
      filters.push(`Location: ${locationParts.join(', ')}`);
    }
  }

  // Property styles
  // The form's default style is 'all' - that is no filter (monday 13121483272).
  const styles = (criteriaConfig.propertyStyles || []).filter(style => style && style !== 'all');
  if (styles.length) {
    filters.push(`Styles: ${styles.join(', ')}`);
  }

  // Price range - only show if actually set (not default 0 or undefined)
  if (
    (criteriaConfig.priceMin !== undefined && criteriaConfig.priceMin !== 0) ||
    (criteriaConfig.priceMax !== undefined && criteriaConfig.priceMax !== 99999999)
  ) {
    const min = criteriaConfig.priceMin
      ? `$${criteriaConfig.priceMin.toLocaleString()}`
      : '$0';
    const max = criteriaConfig.priceMax && criteriaConfig.priceMax !== 99999999
      ? `$${criteriaConfig.priceMax.toLocaleString()}`
      : 'No max';
    filters.push(`Price: ${min} - ${max}`);
  }

  // Square footage - only show if actually set
  if (
    (criteriaConfig.sqftMin !== undefined && criteriaConfig.sqftMin !== 0) ||
    (criteriaConfig.sqftMax !== undefined && criteriaConfig.sqftMax !== 99999)
  ) {
    const min = criteriaConfig.sqftMin
      ? `${criteriaConfig.sqftMin.toLocaleString()} sq ft`
      : '0 sq ft';
    const max = criteriaConfig.sqftMax && criteriaConfig.sqftMax !== 99999
      ? `${criteriaConfig.sqftMax.toLocaleString()} sq ft`
      : 'No max';
    filters.push(`Size: ${min} - ${max}`);
  }

  // Beds range - only show if actually set (not 0 or 'All')
  if (
    (criteriaConfig.bedsMin !== undefined && criteriaConfig.bedsMin !== 0) ||
    (criteriaConfig.bedsMax !== undefined && criteriaConfig.bedsMax !== 0 && criteriaConfig.bedsMax !== 99)
  ) {
    const min = criteriaConfig.bedsMin || 0;
    const max = (criteriaConfig.bedsMax && criteriaConfig.bedsMax !== 99) ? criteriaConfig.bedsMax : 'No max';
    filters.push(`Beds: ${min} - ${max}`);
  }

  // Baths range - only show if actually set
  if (
    (criteriaConfig.bathsMin !== undefined && criteriaConfig.bathsMin !== 0) ||
    (criteriaConfig.bathsMax !== undefined && criteriaConfig.bathsMax !== 0 && criteriaConfig.bathsMax !== 99)
  ) {
    const min = criteriaConfig.bathsMin || 0;
    const max = (criteriaConfig.bathsMax && criteriaConfig.bathsMax !== 99) ? criteriaConfig.bathsMax : 'No max';
    filters.push(`Baths: ${min} - ${max}`);
  }

  // Units range (multifamily) - only show if actually set
  if (
    (criteriaConfig.unitsMin !== undefined && criteriaConfig.unitsMin !== 0) ||
    (criteriaConfig.unitsMax !== undefined && criteriaConfig.unitsMax !== 0 && criteriaConfig.unitsMax !== 99)
  ) {
    const min = criteriaConfig.unitsMin || 0;
    const max = (criteriaConfig.unitsMax && criteriaConfig.unitsMax !== 99) ? criteriaConfig.unitsMax : 'No max';
    filters.push(`Units: ${min} - ${max}`);
  }

  // Buildings range (multifamily) - only show if actually set
  if (
    (criteriaConfig.buildingsMin !== undefined && criteriaConfig.buildingsMin !== 0) ||
    (criteriaConfig.buildingsMax !== undefined && criteriaConfig.buildingsMax !== 0 && criteriaConfig.buildingsMax !== 99)
  ) {
    const min = criteriaConfig.buildingsMin || 0;
    const max = (criteriaConfig.buildingsMax && criteriaConfig.buildingsMax !== 99) ? criteriaConfig.buildingsMax : 'No max';
    filters.push(`Buildings: ${min} - ${max}`);
  }

  // Acreage range (land/commercial) - only show if actually set AND property type is land or commercial
  const propertyType = (criteriaConfig.propertyType || criteriaConfig.propertytype || '').toLowerCase();
  // Acreage/office size arrive as the form's raw strings ('0', '99999'), so compare as numbers.
  const acreageMin = toNumber(criteriaConfig.acreageMin);
  const acreageMax = toNumber(criteriaConfig.acreageMax);
  const officeSqftMin = toNumber(criteriaConfig.officeSqftMin);
  const officeSqftMax = toNumber(criteriaConfig.officeSqftMax);
  if (
    (propertyType === 'land' || propertyType === 'commercial') &&
    ((acreageMin !== undefined && acreageMin !== 0) ||
    (acreageMax !== undefined && acreageMax !== 0 && acreageMax !== 99999))
  ) {
    const min = acreageMin || 0;
    const max = (acreageMax && acreageMax !== 99999) ? acreageMax : 'No max';
    filters.push(`Acreage: ${min} - ${max}`);
  }

  // Year built - only show if actually set
  if (criteriaConfig.yearBuilt !== undefined && criteriaConfig.yearBuilt !== 0) {
    filters.push(`Year Built: ${criteriaConfig.yearBuilt}+`);
  }

  // Office square footage (commercial) - only show if actually set AND property type is commercial
  if (
    propertyType === 'commercial' &&
    ((officeSqftMin !== undefined && officeSqftMin !== 0) ||
    (officeSqftMax !== undefined && officeSqftMax !== 0 && officeSqftMax !== 99999))
  ) {
    const min = officeSqftMin
      ? `${officeSqftMin.toLocaleString()} sq ft`
      : '0 sq ft';
    const max = officeSqftMax && officeSqftMax !== 99999
      ? `${officeSqftMax.toLocaleString()} sq ft`
      : 'No max';
    filters.push(`Office Size: ${min} - ${max}`);
  }

  // Features
  const features: string[] = [];
  if (criteriaConfig.golfCourse) features.push('Golf Course');
  if (criteriaConfig.waterFront) features.push('Waterfront');
  if (criteriaConfig.propertyView) features.push('Property View');
  if (features.length > 0) {
    filters.push(`Features: ${features.join(', ')}`);
  }

  return filters;
}
